import { useCallback, useMemo, useState } from 'react';
import { makeRegisterUserRequestBody } from 'features/signUp/utils/makeRequestBody';
import { useEnrollUserMutation, useRegisterUserMutation } from 'services/signupService';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useAppDispatch } from 'store/store';
import { setSignUpState } from 'features/signUp/signUpSlice';
import { PendoEventType, PendoInitOptions } from 'features/pendo/types';
import {
  setCredentials,
  setIsCaregiverWaitingForApproval,
  setIsLoggedIn,
} from 'features/auth/authSlice';
import { isFetchBaseQueryError } from 'utils';
import { useNotificationSettings } from 'features/settings/hooks/useNotificationSettings';
import {
  isEmailAlreadyRegisteredError,
  isUserCreationFailedError,
} from 'features/signUp/utils/errorUtils';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { updateIsLoggedInLocalStorage } from 'utils/authUtils';
import { EnrollUserResponseBody, RegisterUserResponseBody, SignupFormData } from '../../types';
import { useSignupData } from '../useSignupData';
import { SignupError } from '../useSignupError/SignupError';

export const usePolicyAcceptanceStep = () => {
  const [registerUserQuery] = useRegisterUserMutation();
  const [enrollUserQuery] = useEnrollUserMutation();
  const [isSubmitting, setIsUserStatusUpdatePending] = useState(false);
  const appDispatch = useAppDispatch();
  const {
    hospitalID,
    privacyPolicy,
    eulaPolicy,
    activeUserToken,
    wellframeUserId,
    isUserApproved,
    userSignupStatus,
    ssoAuthConfig,
    isUserInvitedCaregiver,
  } = useSignupData();
  const { handleSignUpCreateNotificationSettings } = useNotificationSettings();
  const { trackEvent } = usePendo();
  const { notificationFlag } = useSignupData();
  const isNotificationFlagEnabled = useMemo(
    () => notificationFlag?.isEnabled ?? false,
    [notificationFlag],
  );
  // Generic Helpers

  /**
   * Updates the Redux store with the user's current approval status.
   * @param updateUserStatusResponseData The response data from the register or enroll user APIs.
   */
  const updateUserApprovalStatus = (
    updateUserStatusResponseData: RegisterUserResponseBody | EnrollUserResponseBody,
  ) => {
    appDispatch(setSignUpState({ isUserApproved: updateUserStatusResponseData.approved_status }));
  };

  const trackPendoSignupEvent = (initOptions: PendoInitOptions) => {
    // Init options are passed to ensure user and hospital ID are available
    // as user data is not yet set in Redux through AuthGuard
    trackEvent(
      PendoEventType.SIGNUP,
      { eventType: PendoEventType.SIGNUP },
      {
        userId: initOptions.userId,
        hospitalId: initOptions.hospitalId,
      },
    );
  };

  // User Registration Helpers

  const shouldRegisterUser = (): boolean =>
    userSignupStatus !== 'registered' && userSignupStatus !== 'enrolled';

  /**
   * Handles errors that occur during the register user API call.
   * @param error The error object from the register user API call.
   */
  const handleRegisterUserError = (error: FetchBaseQueryError | SerializedError) => {
    if (isFetchBaseQueryError(error) && error.status === 'FETCH_ERROR') {
      throw new SignupError('Network error occurred while trying to register user.', 'NoNetwork');
    }

    // error 422, Email already exists error
    if (isEmailAlreadyRegisteredError(error)) {
      throw new SignupError(
        'Registration failed. Email is already registered.',
        'EmailAlreadyRegistered',
      );
    }

    // Other 422 errors
    if (isUserCreationFailedError(error)) {
      throw new SignupError(
        'Registration failed. User could not be created.',
        'UserCreationFailed',
      );
    }

    // Generic error fallback
    throw new SignupError('Registration failed. User could not be created.', 'UserCreationFailed');
  };

  const registerUser = async (
    signupFormData: SignupFormData,
  ): Promise<RegisterUserResponseBody | undefined> => {
    if (!privacyPolicy || !eulaPolicy) {
      throw new SignupError(
        'Privacy policy, and EULA policy must be defined to register a user.',
        'Generic',
      );
    }

    const requestBody = makeRegisterUserRequestBody(
      signupFormData,
      privacyPolicy.effective_date,
      eulaPolicy.effective_date,
      wellframeUserId,
      ssoAuthConfig,
    );

    const { data, error } = await registerUserQuery({
      wellframeUserId: wellframeUserId?.toString(),
      activeUserToken,
      requestBody,
    });

    if (error) {
      handleRegisterUserError(error);
    }

    if (data) {
      updateUserApprovalStatus(data);
      appDispatch(
        setCredentials({
          accessToken: data?.accessToken,
          refreshToken: data?.refreshToken,
        }),
      );
    }

    return data;
  };

  // User Enrollment Helpers

  /**
   * Returns true if the user is approved, but not yet enrolled.
   * @param registerUserData The response data from the register user API call.
   * This is optional because the API call may not have been made if the user has already registered.
   * @returns A boolean indicating whether the user should be enrolled.
   */
  const shouldEnrollUser = (registerUserData?: RegisterUserResponseBody): boolean => {
    // userSignupStatus originates from the Redux store
    const isUserEnrolled =
      registerUserData?.status === 'enrolled' || userSignupStatus === 'enrolled';
    return Boolean(isUserApproved) && !isUserEnrolled;
  };

  /**
   * Handles errors that occur during the enroll user API call.
   * @param error The error object from the enroll user API call.
   */
  const handleEnrollUserError = (error: FetchBaseQueryError | SerializedError) => {
    if (isFetchBaseQueryError(error) && error.status === 'FETCH_ERROR') {
      throw new SignupError('Network error occurred while trying to enroll user.', 'NoNetwork');
    }

    throw new SignupError('An error occurred while trying to enroll user.', 'UserEnrollmentFailed');
  };

  const enrollUser = async () => {
    if (!wellframeUserId) {
      throw new SignupError('Wellframe user ID must be defined to enroll a user.', 'Generic');
    }

    const { data, error } = await enrollUserQuery({
      wellframeUserId: wellframeUserId.toString(),
      activeUserToken,
    });

    if (error) {
      handleEnrollUserError(error);
    }

    if (data) {
      updateUserApprovalStatus(data);
    }
  };
  const { isHospitalSmsEnabled } = useSignupData();
  /** Submits the policy acceptance step. */
  const submit = useCallback(
    async (signupFormData: SignupFormData) => {
      try {
        setIsUserStatusUpdatePending(true);

        let registerUserData: RegisterUserResponseBody | undefined;
        if (shouldRegisterUser()) {
          registerUserData = await registerUser(signupFormData);
          const smsNotificationOptInEnabled = signupFormData.smsOptIn;
          const signUpMemberUuid = registerUserData?.uuid || '';
          const signUpHospitalUuid = registerUserData?.hospital?.uuid || '';
          if (isNotificationFlagEnabled || isHospitalSmsEnabled) {
            handleSignUpCreateNotificationSettings(
              smsNotificationOptInEnabled,
              signUpMemberUuid,
              signUpHospitalUuid,
              Boolean(isHospitalSmsEnabled),
            );
          }
        }

        // Caregiver pending approval
        if (registerUserData && !registerUserData.approved_status) {
          trackPendoSignupEvent({
            userId: registerUserData.id,
            hospitalId: registerUserData.hospital.id,
          });
          appDispatch(setIsCaregiverWaitingForApproval({ value: true, persist: true }));
          return;
        }

        if (shouldEnrollUser(registerUserData)) {
          await enrollUser();
        }

        if (isUserInvitedCaregiver) {
          // If we reach this point with a pre-approved caregiver, we should skip the Welcome screen and log in directly
          appDispatch(setIsLoggedIn(true));
        } else {
          // Update the local storage value instead to redirect them to /home even if they close
          // or refresh on the welcome step
          updateIsLoggedInLocalStorage(true);
        }

        trackPendoSignupEvent({
          userId: registerUserData?.id,
          hospitalId: registerUserData?.hospital.id,
        });
      } catch (error) {
        if (error instanceof SignupError) {
          throw error;
        }

        throw new SignupError(
          'An unexpected error occurred while submitting the policy acceptance step.',
          'UserCreationFailed',
        );
      } finally {
        setIsUserStatusUpdatePending(false);
      }
    },
    [
      privacyPolicy,
      eulaPolicy,
      activeUserToken,
      wellframeUserId,
      isUserApproved,
      userSignupStatus,
      shouldRegisterUser,
      registerUser,
      shouldEnrollUser,
      enrollUser,
    ],
  );

  return {
    /** Indicates whether the policy acceptance step is submitting. */
    isSubmitting,
    /** Submits the policy acceptance step. */
    submit,
  };
};
